import React, { Suspense, useEffect } from "react";

import { Switch } from "react-router-dom";

import { ErrorPage } from "@components-shared/desktop/error/ErrorPage";
import Error404 from "@components/error404/Error404";
import { GlobalLoader } from "@components/global-loader/GlobalLoader";
import ErrorHandler from "@components/layout/ErrorHandler";
import LayoutAppWrapper from "@components/layout/LayoutAppWrapper";
import { NoMatchRoute, RouteElement } from "@components/navigation";
import {
	PopupDisplay,
	WaitingPeriodSlipSubmitPopup,
} from "@components/popups/desktop";
import { RestrictedPage } from "@components/restricted/desktop";
import { AppContextContainer } from "@containers";
import { AccountStatementPage } from "@features/account-statement/desktop";
import AppUpdatedPage from "@features/app-updated-page/desktop/AppUpdatedPage";
import { ChatSupportPage, ChatWrap } from "@features/chat/desktop";
import ConnectionLostPage from "@features/connection-lost/desktop";
import { GamingHistoryPage } from "@features/games/gaming-history/desktop";
import { EmailActivationPage } from "@features/membership/desktop/email-activation";
import { LoginPage } from "@features/membership/desktop/login";
import { RegistrationActivationPage } from "@features/membership/desktop/registration-activation";
import SportsBettingLayout from "@features/offer/desktop/layout/SportsBettingLayout";
import { DepositInfoPage } from "@features/payment/deposit-info/desktop";
import { DepositPage } from "@features/payment/deposit/desktop";
import { PaymentInfoPage } from "@features/payment/payment-info/desktop";
import { WithdrawalPage } from "@features/payment/withdrawal/desktop";
import { PlayerSowPage } from "@features/player-sow/desktop";
import { ResultsPage } from "@features/results/desktop";
import { LoadingContainer } from "@hoc";
import { useRootAppStore } from "@hooks";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { LiveWidgetProvider } from "@state/context/live/live-widgets";
import { useAddTipsFromUrl } from "@state/hooks/offer/useAddTipsFromUrl";
import PrematchOfferMenuStore from "@state/stores/offer/prematch/PrematchOfferMenuStore";
import { THEME_SETTINGS } from "@theme-settings";
//#region lazy imports

import { getCurrentCulture } from "@utils";
import { NotificationContainer } from "@utils/notifications";
import { observer } from "mobx-react";

import OnboardingRegistrationPage from "../membership/onboarding-registration/OnboardingRegistrationPage";
import { PartnerRegistrationPage } from "../membership/partner-registration/PartnerRegistrationPage";
import InfoPagesLayout from "./InfoPagesLayout";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const MyBetSlipSharedPage = lazy(loadFailPath,() => import("@features/my-bets/desktop/bet-slip-shared-page/MyBetSlipSharedPage"));
// prettier-ignore
const MembershipLayout = lazy(loadFailPath, () => import("./MembershipLayout"));
// prettier-ignore
const AccountRecheckPage = lazy(loadFailPath, () => import("@features/account-recheck/desktop/AccountRecheckPage"));
// prettier-ignore
const LimitIncreasePage = lazy(loadFailPath, () => import("@features/limit-increase/desktop/LimitIncreasePage"));
//prettier-ignore
const MyBetsPageLayout = lazy(loadFailPath, () => import("./MyBetsPageLayout"));
// prettier-ignore
const QuickPlayPageLayout = lazy(loadFailPath, () => import("@features/quick-play/desktop/layout/QuickPlayPageLayout"));
//prettier-ignore
const NewsLayout = lazy(loadFailPath, () => import("@features/cms/news/desktop/NewsLayout"));
// prettier-ignore
const AccountSettingsLayout = lazy(loadFailPath,() => import("./AccountSettingsLayout"));
// prettier-ignore
const PlayerInformationCenterPage = lazy(loadFailPath, ()=>import("@features/player-information-center/desktop/PlayerInformationCenterPage"));
//prettier-ignore
const OfferPrintPage = lazy(loadFailPath, () => import("@features/offer-print/OfferPrintPage"));
//prettier-ignore
const UserDataConfirmAuthPage = lazy(loadFailPath, () => import("@features/membership/user-data-confirm/desktop/UserDataConfirmAuthPage"));
//prettier-ignore
const FreebetEmailConfirmation = lazy(loadFailPath, () => import("@features/freebet/desktop/FreebetEmailConfirmation"));
//prettier-ignore
const ScorePredictorLayout = lazy(loadFailPath, () => import("@features/score-predictor/desktop/ScorePredictorLayout"));
//#endregion lazy imports

export default function MainLayoutWrapper() {
	const rootStore = useRootAppStore();
	useEffect(() => {
		rootStore.resizeStore.onInitialize();
		return () => {
			rootStore.resizeStore.onDispose();
		};
	}, []);

	const menuStore = React.useMemo(
		() => new PrematchOfferMenuStore(rootStore),
		[]
	);
	useEffect(() => {
		rootStore.quickPlayStore.quickBetStore.initializeMenuStore(menuStore);

		return () => {
			menuStore.onDispose();
			rootStore.quickPlayStore.quickBetStore.removeMenuStore();
		};
	}, []);
	return (
		<Suspense fallback={<GlobalLoader />}>
			<LiveWidgetProvider
				shouldSuspendWidgets={rootStore.isSomePopupActive}
			>
				<AppContextContainer>
					<NotificationContainer>
						<ErrorPage full>
							<ChatWrap>
								<MainLayout menuStore={menuStore} />
							</ChatWrap>
						</ErrorPage>
					</NotificationContainer>
				</AppContextContainer>
			</LiveWidgetProvider>
		</Suspense>
	);
}

const MainLayout = observer(function MainLayout(props: {
	menuStore: PrematchOfferMenuStore;
}) {
	const rootStore = useRootAppStore();

	useAddTipsFromUrl();

	return (
		<LayoutAppWrapper>
			<LoadingContainer>
				<ErrorHandler />
				<WaitingPeriodSlipSubmitPopup />
				<PopupDisplay />
				<Switch>
					<RouteElement exact path="/:culture/app-update">
						<AppUpdatedPage
							reload={() => {
								window.location.reload();
							}}
						/>
					</RouteElement>
					<RouteElement exact path={["/:culture/results"]}>
						<ResultsPage />
					</RouteElement>

					<RouteElement
						path={[
							"/:culture/home/",
							"/:culture/sports/",
							"/:culture/live/",
							"/:culture/search/",
						]}
					>
						<SportsBettingLayout menuStore={props.menuStore} />
					</RouteElement>

					<RouteElement path={["/:culture/info"]}>
						<InfoPagesLayout />
					</RouteElement>

					<RouteElement
						privateRoute
						path={["/:culture/account-settings/"]}
					>
						<AccountSettingsLayout />
					</RouteElement>

					<RouteElement exact path="/:culture/auth/login">
						<LoginPage />
					</RouteElement>

					{WEBPACK_IS_REGISTRATION_ENABLED && (
						<RouteElement exact path="/:culture/auth/register">
							<OnboardingRegistrationPage />
						</RouteElement>
					)}

					{WEBPACK_IS_REGISTRATION_ENABLED && (
						<RouteElement
							exact
							path="/:culture/auth/register/partner"
						>
							<PartnerRegistrationPage />
						</RouteElement>
					)}

					{WEBPACK_IS_REGISTRATION_ENABLED && (
						<RouteElement
							exact
							path="/:culture/auth/register-activation"
						>
							<RegistrationActivationPage />
						</RouteElement>
					)}

					<RouteElement path={["/:culture/auth"]}>
						<MembershipLayout />
					</RouteElement>

					<RouteElement
						path="/:culture/account-statement"
						privateRoute
					>
						<AccountStatementPage />
					</RouteElement>

					<RouteElement path="/:culture/my-bets" privateRoute>
						<MyBetsPageLayout />
					</RouteElement>

					{THEME_SETTINGS.isPlayerInformationCenterEnabled &&
					rootStore.userAuthStore?.user?.isPICEnabled ? (
						<RouteElement
							exact
							path="/:culture/player-information-center"
						>
							<PlayerInformationCenterPage />
						</RouteElement>
					) : null}

					<RouteElement path="/:culture/bet-slip-shared/:betSlipNumber">
						<MyBetSlipSharedPage />
					</RouteElement>

					{WEBPACK_IS_CASINO_ENABLED ||
					WEBPACK_IS_CASINO_LIVE_ENABLED ? (
						<RouteElement
							path="/:culture/gaming-history"
							privateRoute
						>
							<GamingHistoryPage />
						</RouteElement>
					) : null}

					<RouteElement path="/:culture/payment-info/:status">
						<PaymentInfoPage />
					</RouteElement>

					<RouteElement path="/:culture/deposit" privateRoute>
						<DepositPage />
					</RouteElement>

					<RouteElement path="/:culture/deposit-info" privateRoute>
						<DepositInfoPage />
					</RouteElement>

					<RouteElement path="/:culture/withdrawal" privateRoute>
						<WithdrawalPage />
					</RouteElement>

					<RouteElement path="/:culture/offer">
						<OfferPrintPage />
					</RouteElement>

					<RouteElement path="/:culture/email-activation">
						<EmailActivationPage />
					</RouteElement>

					<RouteElement
						path="/:culture/user-profile-recheck"
						privateRoute
					>
						<AccountRecheckPage />
					</RouteElement>

					<RouteElement path="/:culture/limit-increase" privateRoute>
						<LimitIncreasePage />
					</RouteElement>

					<RouteElement path="/:culture/quick-play">
						<QuickPlayPageLayout />
					</RouteElement>

					<RouteElement
						exact
						path={["/:culture/sow/document-upload"]}
					>
						<PlayerSowPage />
					</RouteElement>

					<RouteElement path="/:culture/error/404">
						<Error404 />
					</RouteElement>

					<RouteElement path="/:culture/restrict">
						<RestrictedPage />
					</RouteElement>

					{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE ? (
						<RouteElement path="/:culture/support-chat">
							<ChatSupportPage />
						</RouteElement>
					) : null}

					<RouteElement path="/:culture/news-announcements">
						<NewsLayout />
					</RouteElement>

					<RouteElement path="/:culture/offline">
						<ConnectionLostPage full />
					</RouteElement>

					<RouteElement path="/:culture/user-data-confirmation">
						<UserDataConfirmAuthPage />
					</RouteElement>

                    <RouteElement path="/:culture/freebet/:action/:freebetId">
						<FreebetEmailConfirmation />
					</RouteElement>
					                                        
                    <RouteElement path="/:culture/score-predictor">
                        <ScorePredictorLayout />
                    </RouteElement>

					<NoMatchRoute full />
				</Switch>
			</LoadingContainer>
		</LayoutAppWrapper>
	);
});
